/**
 * @typedef {object} CrossTabPair
 * @property {string} filterOption - The chosen option of the multiple-choice filter question
 * @property {string} question - Title of the (scale) non-filter question
 * @property {string|number} value - The answer to the non-filter question
 * @property {number} position - Sort position of the non-filter question
 */

/**
 * @typedef {object} CrossTabResult
 * @property {Array<Object<string, number|string>>} chartData - One row per non-filter question: `{ name, position, [filterOption]: avg }`, ordered by position
 * @property {string[]} filterOptions - Distinct filter options across all rows (the chart's bar series keys)
 */

/**
 * Cross-tabulate scale answers by a multiple-choice filter question:
 * for every filter option, the average score per non-filter question.
 *
 * Pure port of the old FilterResults `processMultipleChoiceAndScaleResults` +
 * `combineChartData` + series-key derivation. Non-numeric answers coerce to 0
 * (matching the old `Number(x) || 0`); averages round to one decimal.
 *
 * @param {CrossTabPair[]} pairs - Paired filter-option / non-filter-answer records
 * @returns {CrossTabResult}
 */
const crossTabScaleAverages = (pairs) => {
    const list = Array.isArray(pairs) ? pairs : []
    if (list.length === 0) return { chartData: [], filterOptions: [] }

    // sums[question] = { position, options: { [filterOption]: { total, count } } }
    const sums = {}
    list.forEach(({ filterOption, question, value, position }) => {
        if (!sums[question]) sums[question] = { position: position || 0, options: {} }
        if (!sums[question].options[filterOption]) {
            sums[question].options[filterOption] = { total: 0, count: 0 }
        }
        sums[question].options[filterOption].total += Number(value) || 0
        sums[question].options[filterOption].count += 1
    })

    const chartData = Object.entries(sums)
        .map(([question, { position, options }]) => {
            const row = { name: question, position }
            Object.entries(options).forEach(([option, { total, count }]) => {
                row[option] = count > 0 ? parseFloat((total / count).toFixed(1)) : 0
            })
            return row
        })
        .sort((a, b) => a.position - b.position)

    const filterOptions = Array.from(
        new Set(chartData.flatMap((row) =>
            Object.keys(row).filter((key) => key !== 'name' && key !== 'position')
        ))
    )

    return { chartData, filterOptions }
}

export default crossTabScaleAverages
